import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {
  Heart,
  Target,
  Shield,
  Users,
  Award,

  ArrowRight,
  Star,
  CheckCircle,
  Calendar,
  Microscope,

  Linkedin,
  Twitter
} from 'lucide-react'
import PageLayout from '../components/layout/PageLayout'
import dhruvProfileImage from '../assets/dhruv_parikh_profile.jpg'

const About: React.FC = () => {
  const values = [
    {
      icon: Heart,
      title: "Patient-First Approach",
      description: "Every decision we make puts our users' health and wellbeing first. We believe in transparent, honest communication about health data."
    },
    {
      icon: Target,
      title: "Precision Health",
      description: "We combine cutting-edge technology with medical expertise to deliver precise, personalized health insights that drive real results."
    },
    {
      icon: Shield,
      title: "Privacy & Security",
      description: "We maintain the highest standards of data protection and HIPAA compliance to keep your health information secure."
    },
    {
      icon: Award,
      title: "Scientific Excellence",
      description: "Our methods are backed by peer-reviewed research and validated by medical professionals to ensure accuracy and reliability."
    }
  ]

  const team = [
    {
      name: "Dhruv Parikh",
      role: "Founder, CEO, Developer & Researcher",
      image: dhruvProfileImage,
      bio: "Visionary entrepreneur building the future of personalized health optimization through integrated AI and biomarker analytics.",
      linkedin: "https://www.linkedin.com/in/dhruva-parikh/",
      twitter: "#"
    }
  ]

  // ... (milestones, stats, etc.) ...

  const milestones = [
    {
      year: "2025",
      title: "Company Founded",
      description: "BioTune was established to make advanced health analytics accessible to everyone."
    },
    {
      year: "Q2 2026",
      title: "CLIA Lab Partnership Target",
      description: "Targeting certified laboratory partnerships by Q2 2026 to ensure the highest testing standards."
    },
    {
      year: "Q4 2026",
      title: "AI Platform Launch",
      description: "Launching our proprietary AI platform for personalized health insights and recommendations."
    },
    {
      year: "Q1 2027",
      title: "10,000 Users Plan",
      description: "Plan to reach 10,000 users by Q1 2027 while expanding our biomarker panel."
    },
    {
      year: "2027-2028",
      title: "AI Health Kiosks Pilot",
      description: "Deploying autonomous AI-powered health screening kiosks in high-traffic locations (malls, transit hubs, gyms) for 24/7 accessible biomarker testing and instant health insights."
    }
  ]

  const stats = [
    {
      number: "Early",
      label: "Pilot Members",
      icon: Users
    },
    {
      number: "100+",
      label: "Biomarkers Planned",
      icon: Microscope
    },
    {
      number: "High",
      label: "Satisfaction Goal",
      icon: Star
    },
    {
      number: "5-7",
      label: "Days for Results (Target)",
      icon: Calendar
    }
  ]

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="section-padding bg-theme-surface">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text gradient-heading mb-6">
              Revolutionizing Personal Health
            </h1>
            <p className="text-xl md:text-2xl text-theme-secondary mb-8 leading-relaxed">
              BioTune combines advanced biomarker testing with AI-powered insights to help people optimize their health and extend their healthy lifespan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth/signup">
                <Button size="lg" variant="default">
                  Join Our Mission
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
                className="text-center group"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 bg-theme-surface rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <stat.icon className="w-8 h-8 text-theme-primary group-hover:animate-spin" />
                </div>
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent mb-2">{stat.number}</div>
                <div className="text-theme-secondary font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-theme-surface">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-theme-secondary mb-8">
                To empower individuals with the knowledge and tools they need to optimize their health and extend their healthy lifespan through advanced biomarker analysis and personalized insights.
              </p>
              <div className="space-y-6">
                <motion.div
                  className="flex items-start space-x-4 p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-theme-success/20 to-theme-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-theme-success" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-theme-primary mb-1">Preventive Health Focus</h3>
                    <p className="text-theme-secondary">We believe in catching health issues before they become problems through regular monitoring and early intervention.</p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex items-start space-x-4 p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-theme-primary/20 to-theme-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-theme-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-theme-primary mb-1">Personalized Approach</h3>
                    <p className="text-theme-secondary">Every person is unique, and their health optimization plan should be too. We tailor recommendations to your specific biomarker profile.</p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex items-start space-x-4 p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-theme-secondary/20 to-theme-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-theme-secondary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-theme-primary mb-1">Scientific Excellence</h3>
                    <p className="text-theme-secondary">All our recommendations are backed by peer-reviewed research and validated by medical professionals.</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative"
            >
              <img
                src="https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Modern%20medical%20team%20in%20laboratory%2C%20diverse%20group%20of%20scientists%20and%20doctors%2C%20professional%20healthcare%20setting%2C%20clean%20and%20bright%20environment%2C%20high%20quality%20medical%20photography&image_size=landscape_16_9"
                alt="BioTune Team"
                className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
              />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              These principles guide everything we do at BioTune.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
                whileHover={{ scale: 1.02 }}
              >
                <Card className="h-full bg-theme-surface border border-theme-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <div className="w-14 h-14 bg-theme-surface rounded-xl flex items-center justify-center mb-4 shadow-lg">
                      <value.icon className="w-7 h-7 text-theme-primary group-hover:animate-spin" />
                    </div>
                    <CardTitle className="text-xl text-theme-primary">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-theme-secondary">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-theme-surface">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
              Key milestones in BioTune's development and growth.
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-200 to-teal-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6, ease: "easeOut" }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <Card className="bg-theme-surface border border-theme-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <CardHeader>
                        <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-2">{milestone.year}</div>
                        <CardTitle className="text-xl text-theme-primary">{milestone.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-theme-secondary">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="relative z-10">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
              Meet the founder building BioTune's innovative health analytics platform.
            </p>
          </div>
          <div className="flex justify-center">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
                className="text-center"
                whileHover={{ scale: 1.05 }}
              >
                <Card className="h-full bg-theme-surface border border-theme-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto mb-4 object-cover transform hover:scale-110 transition-transform duration-300"
                    />
                    <CardTitle className="text-xl text-theme-primary">{member.name}</CardTitle>
                    <p className="text-blue-600 font-bold">{member.role}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-theme-secondary text-sm mb-4">{member.bio}</p>
                    <div className="flex justify-center space-x-4">
                      <a href={member.linkedin} className="text-theme-muted hover:text-blue-600 transition-colors">
                        <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                          <Linkedin className="w-5 h-5" />
                        </motion.div>
                      </a>
                      <a href={member.twitter} className="text-theme-muted hover:text-blue-600 transition-colors">
                        <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                          <Twitter className="w-5 h-5" />
                        </motion.div>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-theme-primary">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
              Join Us in Revolutionizing Healthcare
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
              Be part of the future of preventive health. Start your journey with BioTune today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth/signup">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl hover:shadow-2xl" size="lg">
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 shadow-xl" size="lg">
                  View Careers
                </Button>
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>
    </PageLayout>
  )
}

export default About
